import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const TravelInsights = ({ insights }) => {
  const monthlySpendingData = [
    { month: 'Jan', amount: 1200 },
    { month: 'Feb', amount: 800 },
    { month: 'Mar', amount: 2100 },
    { month: 'Apr', amount: 1500 },
    { month: 'May', amount: 3200 },
    { month: 'Jun', amount: 2800 }
  ];

  const categoryData = [
    { name: 'Adventure', value: 35, color: '#1B4B66' },
    { name: 'Cultural', value: 25, color: '#E8B86D' },
    { name: 'Beach', value: 20, color: '#C7522A' },
    { name: 'City Tours', value: 15, color: '#059669' },
    { name: 'Food & Drink', value: 5, color: '#D97706' }
  ];

  const insightCards = [
    {
      id: 'favorite-destination',
      title: 'Favorite Destination',
      value: insights?.favoriteDestination,
      icon: 'MapPin',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'average-trip-cost',
      title: 'Average Trip Cost',
      value: `$${insights?.averageTripCost?.toLocaleString()}`,
      icon: 'DollarSign',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      id: 'travel-frequency',
      title: 'Trips This Year',
      value: insights?.tripsThisYear,
      icon: 'Calendar',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 'next-milestone',
      title: 'Next Milestone',
      value: insights?.nextMilestone,
      icon: 'Trophy',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Insight Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {insightCards?.map((card) => (
          <div key={card?.id} className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${card?.bgColor}`}>
                <Icon name={card?.icon} size={16} className={card?.color} />
              </div>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{card?.value}</p>
              <p className="text-xs text-muted-foreground">{card?.title}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Spending Chart */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-semibold text-foreground mb-4">Monthly Spending</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlySpendingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6B7280"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#6B7280"
                  fontSize={12}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  formatter={(value) => [`$${value}`, 'Amount']}
                  labelStyle={{ color: '#1A1D23' }}
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="amount" fill="#1B4B66" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Travel Categories Chart */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-semibold text-foreground mb-4">Travel Categories</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {categoryData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  labelStyle={{ color: '#1A1D23' }}
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4">
            {categoryData?.map((item) => (
              <div key={item?.name} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item?.color }}
                ></div>
                <span className="text-xs text-muted-foreground">{item?.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Travel Goals */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="font-semibold text-foreground mb-4">Travel Goals Progress</h3>
        <div className="space-y-4">
          {insights?.goals?.map((goal) => (
            <div key={goal?.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{goal?.title}</span>
                <span className="text-sm text-muted-foreground">
                  {goal?.current}/{goal?.target}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((goal?.current / goal?.target) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{Math.round((goal?.current / goal?.target) * 100)}% complete</span>
                <span>{goal?.target - goal?.current} remaining</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recommendations */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="font-semibold text-foreground mb-4">Personalized Recommendations</h3>
        <div className="space-y-3">
          {insights?.recommendations?.map((recommendation, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
              <div className="p-1 bg-primary/10 rounded-full">
                <Icon name="Lightbulb" size={14} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">{recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TravelInsights;